import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Code2, Rocket, Layers, FolderKanban, Lightbulb } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.15, ease: [0.25, 0.46, 0.45, 0.94] }
  })
};

const skills = [
  'HTML / CSS', 'JavaScript', 'React', 'Node.js',
  'Python', 'SQL', 'Git', 'UI/UX Design',
  'REST APIs', 'Responsive Design'
];

const cards = [
  {
    icon: GraduationCap,
    title: 'Education',
    desc: 'Successfully completed UG Diploma in Computer Science & Engineering with hands-on project experience.'
  },
  {
    icon: Rocket,
    title: "What's Next",
    desc: 'Gearing up for B.Tech to deepen my expertise in software engineering and computer science.'
  },
  {
    icon: Code2,
    title: 'Development',
    desc: 'Building responsive, accessible web applications with modern frameworks and best practices.'
  },
  {
    icon: Layers,
    title: 'Design',
    desc: 'Passionate about crafting pixel-perfect interfaces with glassmorphism, animations, and modern aesthetics.'
  },
  {
    icon: FolderKanban,
    title: 'Final Year Project',
    desc: 'Built the GPT Kampli Library Management System as my diploma final year project — a full-stack real-world application.'
  },
  {
    icon: Lightbulb,
    title: 'Always Learning',
    desc: 'Constantly exploring new technologies, tools, and frameworks to stay ahead of the curve.'
  },
];

const timeline = [
  { date: '2026 →', title: 'B.Tech in CSE', desc: 'Starting bachelor\'s degree to advance knowledge in computer science and software engineering.' },
  { date: '2023 – 2026', title: 'UG Diploma in CSE', desc: 'Completed diploma with strong foundations in programming, databases, and web technologies.' },
  { date: '2025 – 2026', title: 'Final Year Project', desc: 'Designed and developed the GPT Kampli Library Management System — a comprehensive full-stack application.' },
];

const About = () => {
  return (
    <section id="about" className="section-wrapper">
      {/* Section Header */}
      <motion.div
        className="section-header"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, margin: "-80px" }}
        variants={fadeUp}
        custom={0}
      >
        <span className="section-label">About Me</span>
        <h2 className="section-title">
          Crafting Digital <span className="gradient-text">Experiences</span>
        </h2>
        <p className="section-subtitle">
          I'm a passionate tech enthusiast who completed my UG Diploma in CSE
          and now transitioning to B.Tech, with a love for building beautiful, functional web applications.
        </p>
      </motion.div>

      {/* Cards Grid */}
      <div className="about-grid">
        {cards.map((card, i) => (
          <motion.div
            key={i}
            className="glass about-card"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, margin: "-50px" }}
            variants={fadeUp}
            custom={i}
          >
            <div className="about-card-icon">
              <card.icon size={28} />
            </div>
            <h3>{card.title}</h3>
            <p>{card.desc}</p>
          </motion.div>
        ))}
      </div>

      {/* Timeline */}
      <motion.div
        className="section-header"
        style={{ marginTop: '100px' }}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        variants={fadeUp}
        custom={0}
      >
        <span className="section-label">Journey</span>
        <h2 className="section-title">
          My <span className="gradient-text">Timeline</span>
        </h2>
      </motion.div>

      <div className="timeline">
        {timeline.map((item, i) => (
          <motion.div
            key={i}
            className="timeline-item"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, margin: "-50px" }}
            variants={fadeUp}
            custom={i}
          >
            <div className="timeline-dot" />
            <div className="timeline-date">{item.date}</div>
            <div className="timeline-title">{item.title}</div>
            <div className="timeline-desc">{item.desc}</div>
          </motion.div>
        ))}
      </div>

      {/* Skills */}
      <motion.div
        className="section-header"
        style={{ marginTop: '100px' }}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        variants={fadeUp}
        custom={0}
      >
        <span className="section-label">Tech Stack</span>
        <h2 className="section-title">
          Skills & <span className="gradient-text">Technologies</span>
        </h2>
      </motion.div>

      <motion.div
        className="skills-grid"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
      >
        {skills.map((skill, i) => (
          <motion.span
            key={i}
            className="skill-tag"
            variants={fadeUp}
            custom={i * 0.5}
          >
            {skill}
          </motion.span>
        ))}
      </motion.div>
    </section>
  );
};

export default About;
